import React, {Component} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import moment from 'moment-timezone';
import Button from '@spothero/ui/button';
import DateTimeRangePicker from '@spothero/ui/date-time-range-picker';
import Label from '@spothero/ui/label';
import Tooltip from '@spothero/ui/tooltip';
import DateUtils from 'utils/date';
import TimeUtils from 'utils/time';
import withConfig from 'config/withConfig';

const defaultTimePickerFormat = 'h:mm A';

class AirportDateTimePickers extends Component {
    static propTypes = {
        className: PropTypes.string,
        /** A datetime in the format YYYY-MM-DDTHH:mm. */
        starts: PropTypes.string,
        /** A datetime in the format YYYY-MM-DDTHH:mm. */
        ends: PropTypes.string,
        isAdmin: PropTypes.bool.isRequired,
        startDateLabelText: PropTypes.string,
        endDateLabelText: PropTypes.string,
        startTimeLabelText: PropTypes.string,
        endTimeLabelText: PropTypes.string,
        displayDateFormat: PropTypes.string.isRequired,
        standardTimeFormat: PropTypes.string.isRequired,
        timezone: PropTypes.string.isRequired,
        apiDateTimeFormat: PropTypes.string,
        useCalendarWithShim: PropTypes.bool,
        isLoading: PropTypes.bool,
        searchButtonText: PropTypes.string,
        onSearchClick: PropTypes.func,
    };
    static defaultProps = {
        starts: null,
        ends: null,
        searchButtonText: 'Search',
    };

    constructor(props) {
        super(props);

        const {starts, ends, displayDateFormat, standardTimeFormat} = props;
        const startsMoment = starts ? moment(starts) : null;
        const endsMoment = ends ? moment(ends) : null;
        const startDate = startsMoment
            ? startsMoment.format(displayDateFormat)
            : '';
        const startTime = startsMoment
            ? startsMoment.format(standardTimeFormat)
            : '';
        const endDate = endsMoment ? endsMoment.format(displayDateFormat) : '';
        const endTime = endsMoment ? endsMoment.format(standardTimeFormat) : '';

        this.state = {
            startTooltipMessage: null,
            startDate,
            startTime,
            endDate,
            endTime,
            isValid: Boolean(startDate && startTime && endDate && endTime),
            areAllFieldsEmpty: false,
        };
    }

    _onDateTimeRangePickerInvalid = () => {
        this.setState({
            isValid: false,
        });
    };

    _onChange = ({startDate, startTime, endDate, endTime}) => {
        const {isAdmin, timezone} = this.props;
        const newState = {
            startDate,
            startTime,
            endDate,
            endTime,
            isValid: Boolean(startDate && startTime && endDate && endTime),
            areAllFieldsEmpty: Boolean(
                !startDate && !startTime && !endDate && !endTime
            ),
        };

        if (!isAdmin && startDate && startTime) {
            const starts = DateUtils.combineDateAndTime(
                DateUtils.getAsMoment(startDate),
                TimeUtils.getAsMoment(startTime)
            );
            const {isNewStart} = TimeUtils.checkStaleStart(
                starts.clone(),
                timezone
            );

            if (isNewStart) {
                newState.startTooltipMessage =
                    'Cannot search for more than 30 minutes in the past.';
                newState.isValid = false;
            } else {
                newState.startTooltipMessage = null;
            }
        }

        this.setState(newState);
    };

    _onStartTooltipHidden = () => {
        this.setState({
            startTooltipMessage: null,
        });
    };

    _onSearchClick = () => {
        const {onSearchClick, apiDateTimeFormat} = this.props;
        const {startDate, startTime, endDate, endTime, isValid} = this.state;

        if (isValid) {
            if (onSearchClick) {
                onSearchClick({
                    starts: DateUtils.combineDateAndTime(
                        DateUtils.getAsMoment(startDate),
                        TimeUtils.getAsMoment(startTime)
                    ).format(apiDateTimeFormat),
                    ends: DateUtils.combineDateAndTime(
                        DateUtils.getAsMoment(endDate),
                        TimeUtils.getAsMoment(endTime)
                    ).format(apiDateTimeFormat),
                });
            }
        } else {
            this.setState({
                areAllFieldsEmpty: Boolean(
                    !startDate && !startTime && !endDate && !endTime
                ),
            });
        }
    };

    render() {
        const {
            className,
            useCalendarWithShim,
            startDateLabelText,
            endDateLabelText,
            startTimeLabelText,
            endTimeLabelText,
            isLoading,
            searchButtonText,
        } = this.props;
        const {
            startDate,
            startTime,
            endDate,
            endTime,
            startTooltipMessage,
            isValid,
            areAllFieldsEmpty,
        } = this.state;
        const containerClasses = classNames(
            'AirportDateTimePickers',
            className
        );

        return (
            <div className={containerClasses}>
                <DateTimeRangePicker
                    startId="AirportDateTimePickers-start"
                    endId="AirportDateTimePickers-end"
                    startDateLabel={
                        startDateLabelText && (
                            <Label>{startDateLabelText}</Label>
                        )
                    }
                    endDateLabel={
                        endDateLabelText && <Label>{endDateLabelText}</Label>
                    }
                    startTimeLabel={
                        startTimeLabelText && (
                            <Label>{startTimeLabelText}</Label>
                        )
                    }
                    endTimeLabel={
                        endTimeLabelText && <Label>{endTimeLabelText}</Label>
                    }
                    startDateDisplayFormat={
                        DateUtils.getTransientDateDisplayFormat
                    }
                    startTimeDisplayFormat={defaultTimePickerFormat}
                    startDate={startDate}
                    startTime={startTime}
                    endDateDisplayFormat={
                        DateUtils.getTransientDateDisplayFormat
                    }
                    endTimeDisplayFormat={defaultTimePickerFormat}
                    endDate={endDate}
                    endTime={endTime}
                    endTimeBump={180}
                    onStartChange={this._onChange}
                    onEndChange={this._onChange}
                    useTetherShim={useCalendarWithShim}
                    onInvalid={this._onDateTimeRangePickerInvalid}
                    areAllFieldsEmpty={areAllFieldsEmpty}
                    autoBumpInvalidEnabled
                />
                {startTooltipMessage && (
                    <Tooltip
                        triggerSelector=".AirportDateTimePickers .DateTimePicker-start select"
                        position="bottom"
                        updateOnMove
                        onHidden={this._onStartTooltipHidden}
                    >
                        {startTooltipMessage}
                    </Tooltip>
                )}
                <Button
                    className="AirportDateTimePickers-submit"
                    color="primary"
                    loading={isLoading}
                    disabled={!isValid}
                    onClick={this._onSearchClick}
                >
                    {searchButtonText}
                </Button>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {
            data: {timezone},
        },
        user: {
            data: {isAdmin},
        },
    } = state;

    return {
        isAdmin,
        timezone,
    };
};

const enhance = compose(
    withConfig([
        'apiDateTimeFormat',
        'displayDateFormat',
        'standardTimeFormat',
    ]),
    connect(mapStateToProps)
);

export default enhance(AirportDateTimePickers);
